import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { CreditCard, Bell, AlertCircle } from 'lucide-react';

/**
 * HARD-BLOCKER modal shown after a customer chooses EFTPOS payment.
 *
 * Unlike the cash flow ("customer walks to the counter"), EFTPOS is a
 * managed-comes-to-you flow:
 *   - Customer taps EFTPOS at checkout → order is placed (payment_status
 *     stays 'pending') → a `payment_eftpos` service-request fires →
 *     this modal stays mounted until staff taps "Paid (Card)" on the
 *     Orders page (or voids the order).
 *
 * Same dismissability contract as `CashCounterModal`: customer CANNOT
 * close it. The only escape hatch is the in-modal "Ring service bell"
 * button (which simply re-fires the same service request — useful if
 * the manager hasn't shown up after a couple of minutes).
 *
 * Props:
 *   alert: { orderId, name, total } | null
 *   onRingBell: () => void
 */
export default function EftposTerminalModal({ alert, onRingBell }) {
  return (
    <Dialog open={!!alert} onOpenChange={() => {}}>
      <DialogContent
        className="bg-bg-primary border-2 border-blue-accent/70 max-w-sm shadow-[0_0_40px_rgba(96,165,250,0.45)] [&>button.absolute]:hidden"
        data-testid="eftpos-terminal-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-white text-center text-xl sm:text-2xl">
            <CreditCard className="w-12 h-12 text-blue-accent mx-auto mb-3" />
            Time to Collect Payment via EFTPOS
          </DialogTitle>
        </DialogHeader>
        <div className="text-center pt-2 space-y-4">
          <p className="text-zinc-300 font-sans text-sm leading-relaxed">
            {alert?.name ? `Thanks ${(alert.name || '').split(' ')[0]}! ` : ''}
            A manager is on the way with the EFTPOS terminal.
            <span className="block mt-2 text-3xl font-mono text-gold font-semibold">
              ${(alert?.total || 0).toFixed(2)}
            </span>
          </p>

          <div className="bg-blue-accent/10 border border-blue-accent/30 rounded-sm p-3">
            <p className="text-[11px] uppercase tracking-[0.2em] text-blue-accent font-sans mb-1">Order reference</p>
            <p className="font-mono text-base text-white">#{(alert?.orderId || '').slice(0, 8).toUpperCase()}</p>
          </div>

          <div className="flex items-start gap-2 text-left bg-blue-accent/5 border border-blue-accent/20 rounded-sm p-3">
            <AlertCircle className="w-4 h-4 text-blue-accent flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-accent/90 font-sans leading-relaxed">
              Please stay seated — your manager will bring the EFTPOS terminal to your table.
              This message will clear automatically once payment is confirmed.
            </p>
          </div>

          {onRingBell && (
            <Button
              onClick={onRingBell}
              variant="outline"
              className="w-full bg-blue-accent/15 border border-blue-accent/60 text-blue-accent hover:bg-blue-accent/25 rounded-sm font-sans shadow-[0_0_18px_rgba(96,165,250,0.55)] animate-[majnu-bell-pulse_1.4s_ease-in-out_infinite]"
              data-testid="eftpos-terminal-ring-bell"
            >
              <Bell className="w-4 h-4 mr-2 animate-[majnu-bell-shake_1.2s_ease-in-out_infinite]" />
              Ring service bell again
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
